import type { ReactElement, ReactNode } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";

import { AppLayout } from "./components/AppLayout";
import { FullscreenLoader } from "./components/FullscreenLoader";
import { useAuth } from "./auth/AuthContext";
import { homePathFor, NAV_ITEMS, navForRoles, type NavItem } from "./nav/navigation";

/**
 * Раздел «Верификация» — им же охраняется карточка отдельного черновика (`/verification/:id`).
 * Берётся ИЗ СПИСКА разделов, а не переписывается ролями рядом: разойдись эти два перечня, и
 * карточка осталась бы открытой роли, у которой раздела нет.
 */
const VERIFY_ITEM: NavItem = NAV_ITEMS.find((i) => i.id === "verify")!;
import { AuditPage } from "./pages/AuditPage";
import { CalcPrintPage } from "./pages/CalcPrintPage";
import { ChangePasswordPage } from "./pages/ChangePasswordPage";
import { LoginAttemptsPage } from "./pages/LoginAttemptsPage";
import { ContractCardPage, ContractCreatePage } from "./pages/ContractCard";
import { ContractsPage } from "./pages/ContractsPage";
import { CounterpartiesPage } from "./pages/CounterpartiesPage";
import { DashboardPage } from "./pages/DashboardPage";
import { HelpArticlePage } from "./pages/HelpArticlePage";
import { HelpPage } from "./pages/HelpPage";
import { LegalPage } from "./pages/LegalPage";
import { LoginPage } from "./pages/LoginPage";
import { ObligationsRegistryPage } from "./pages/ObligationsRegistryPage";
import { RegisterPage } from "./pages/RegisterPage";
import { SECTION_META, SectionPlaceholder } from "./pages/SectionPlaceholder";
import { VerificationListPage } from "./pages/VerificationListPage";
import { VerifyPage } from "./pages/VerifyPage";
import { SettingsPage } from "./pages/SettingsPage";
import { SummaryPage } from "./pages/SummaryPage";

/**
 * Корень SPA (SK-5): пока выясняется сессия — индикатор; дальше маршрутизация.
 * Защищённые разделы обёрнуты в <ProtectedRoute> (без входа — на /login) и, поверх,
 * в <SectionGuard> по ролям (UI-скрытие; серверный запрет — E1).
 */
export function App() {
  const { status } = useAuth();

  if (status === "loading") return <FullscreenLoader />;

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route element={<ProtectedRoute />}>
        <Route index element={<HomeRedirect />} />
        {/* Смена своего пароля (D-15). Раздела навигации у неё нет — вход через ссылку в
            шапке; при поднятом `mustChangePassword` сюда уводит <ProtectedRoute>. */}
        <Route path="/change-password" element={<ChangePasswordPage />} />
        {NAV_ITEMS.map((item) => (
          <Route
            key={item.id}
            path={item.path}
            element={<SectionGuard item={item}>{sectionElement(item)}</SectionGuard>}
          />
        ))}
        {/* Карточка договора (E5): просмотр — всем ролям (как реестр); создание внутри само
            уводит роли без права `contract:register`. Права на правку/спецификации/архив —
            на сервере (матрица 4.3). Статичный `/new` объявлен до параметрического `/:id`. */}
        <Route path="/contracts/new" element={<ContractCreatePage />} />
        <Route path="/contracts/:id" element={<ContractCardPage />} />
        {/* Карточка черновика ИИ-разбора (D-24). Раздел «Верификация» — это СПИСОК, а
            конкретный черновик открывается адресом: из строки списка и из сигнала
            колокольчика. Роли — те же, что у раздела (строка 4.3 «Верификация черновиков»);
            настоящий запрет держит сервер (правило 5). */}
        <Route
          path="/verification/:id"
          element={
            <SectionGuard item={VERIFY_ITEM}>
              <VerifyPage />
            </SectionGuard>
          }
        />
        {/* Статья справки (E16). Ролевого гарда у неё нет намеренно — как и у раздела:
            статьи общие, персональных данных в них нет, а понять, почему коллега видит
            другое, полезно. Неизвестный адрес перехватывает сама страница и объясняет. */}
        <Route path="/help/:articleId" element={<HelpArticlePage />} />
        <Route path="*" element={<NotFound />} />
      </Route>

      {/* Печатные формы (E9a) — БЕЗ рабочего интерфейса: на лист идёт документ, а не экран
          приложения. Вход по-прежнему обязателен (<ProtectedBare>), а право проверяет сервер
          (`registry:view` — то же, что просмотр расчёта; новых прав эпик не заводит). */}
      <Route element={<ProtectedBare />}>
        <Route path="/print/calc/obligation/:oblId" element={<CalcPrintPage />} />
        <Route path="/print/calc/claim/:claimId" element={<CalcPrintPage />} />
      </Route>
    </Routes>
  );
}

/** Вошедшего пускает на страницу без шапки и навигации; анонимного — на вход. */
function ProtectedBare() {
  return useAuthGate() ?? <Outlet />;
}

/**
 * Общая проверка входа для обеих защищённых веток (с интерфейсом и без него): пока сессия
 * выясняется — индикатор, анонимного — на вход с запоминанием исходного пути. Возвращает `null`,
 * когда пускать можно. Одно место на два маршрутных дерева: правила входа меняются здесь.
 */
function useAuthGate(): ReactElement | null {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "loading") return <FullscreenLoader />;
  if (status === "anonymous") {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return null;
}

/**
 * Наполнение раздела по его id. Реализованные разделы отдают свою страницу; остальные —
 * заглушку с пояснением, какой эпик их наполнит (сквозной скелет без имитации данных).
 */
function sectionElement(item: NavItem): ReactElement {
  if (item.id === "summary") return <SummaryPage />;
  if (item.id === "dashboard") return <DashboardPage />;
  if (item.id === "obligations") return <ObligationsRegistryPage />;
  if (item.id === "contracts") return <ContractsPage />;
  if (item.id === "counterparties") return <CounterpartiesPage />;
  if (item.id === "settings") return <SettingsPage />;
  if (item.id === "audit") return <AuditPage />;
  if (item.id === "login-audit") return <LoginAttemptsPage />;
  if (item.id === "legal") return <LegalPage />;
  if (item.id === "register") return <RegisterPage />;
  if (item.id === "verify") return <VerificationListPage />;
  if (item.id === "help") return <HelpPage />;
  return <SectionPlaceholder meta={SECTION_META[item.id] ?? { title: item.label, note: "" }} />;
}

/** Пускает в раздел только вошедших; иначе — на вход, запомнив исходный путь. */
function ProtectedRoute() {
  const { user } = useAuth();
  const location = useLocation();
  const gate = useAuthGate();
  if (gate !== null) return gate;

  // D-15: пароль выдан администратором и ещё не сменён — кроме смены пароля идти некуда.
  // Это ЗЕРКАЛО серверного гарда, а не сам запрет: сервер отвечает 403 на всё остальное
  // независимо от того, что покажет интерфейс (правило 5). Здесь — чтобы человек видел
  // объяснение и форму, а не череду отказов.
  const onChangePassword = location.pathname === "/change-password";
  if (user?.mustChangePassword && !onChangePassword) {
    return <Navigate to="/change-password" replace />;
  }

  // Пользователь без единой роли (может появиться при CRUD пользователей в E1): показываем
  // понятное объяснение, а не пустой экран/редирект-на-себя (нав-панель у него пуста).
  // Смена своего пароля — исключение: она не раздел, и доступна в том числе учётке без
  // ролей (иначе такой пользователь с флагом D-15 попал бы в тупик — увели на смену пароля,
  // а вместо формы «нет доступных разделов»).
  if (!onChangePassword && navForRoles(user?.roles ?? []).length === 0) {
    return (
      <AppLayout>
        <NoSections />
      </AppLayout>
    );
  }
  return (
    <AppLayout>
      <Outlet />
    </AppLayout>
  );
}

/** Стартовый раздел роли (клерк не видит «Сводку» → попадёт на «Договоры»). */
function HomeRedirect() {
  const { user } = useAuth();
  return <Navigate to={homePathFor(user?.roles ?? [])} replace />;
}

function NoSections() {
  return (
    <section aria-labelledby="no-sections-title">
      <h1 id="no-sections-title" className="text-[20px] font-bold text-ink">
        Нет доступных разделов
      </h1>
      <p className="mt-3 text-muted">
        Вашей учётной записи не назначено ни одной роли. Обратитесь к администратору системы.
      </p>
    </section>
  );
}

/** Ролевой доступ к разделу: нет пересечения ролей — возвращаем на стартовый раздел. */
function SectionGuard({ item, children }: { item: NavItem; children: ReactNode }) {
  const { user } = useAuth();
  const roles = user?.roles ?? [];
  const allowed = item.roles.some((r) => roles.includes(r));
  return allowed ? <>{children}</> : <Navigate to={homePathFor(roles)} replace />;
}

function NotFound() {
  return (
    <section aria-labelledby="nf-title">
      <h1 id="nf-title" className="text-[20px] font-bold text-ink">
        Раздел не найден
      </h1>
      <p className="mt-3 text-muted">Такой страницы нет. Выберите раздел в панели слева.</p>
    </section>
  );
}
